import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce } from 'react-use'
import get from 'lodash/get'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import { makeStyles, Theme } from 'transfix-ui/core/styles'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { ControlledTrailer } from 'apps/fleet-planner/components/create-load/ControlledTrailer'
import { ControlledTruck } from 'apps/fleet-planner/components/create-load/ControlledTruck'
import { SectionHeaderText } from 'apps/fleet-planner/components/create-load/SectionHeaderText'
import { useCreateLoadFormContext } from 'apps/fleet-planner/context'
import { useMembershipValidation } from 'apps/fleet-planner/hooks/useMembershipValidation'
import { Driver } from 'apps/fleet-planner/pages/loads/Driver'
import { CreateLoadSchemaType, IShipmentMotorCarrier } from 'apps/fleet-planner/schema'
import { ILoad } from 'apps/fleet-planner/types'
import { fetchCarriers } from 'apps/fleet-planner/utils/actions/carriers'
import { fetchEquipment } from 'apps/fleet-planner/utils/actions/equipment'
import { formatCarrier } from 'apps/fleet-planner/utils/api/carriers'
import { dispatchersListSelector, selectCarriersList } from 'apps/fleet-planner/utils/selectors'
import { Checkbox } from 'components/forms'
import { ControlledCarrier } from './ControlledCarrier'
import { ControlledDispatcher } from './ControlledDispatcher'

interface IUseSectionBasicsStylesProps {
  isCarrierInputVisible: boolean
}

const useSectionBasicsStyles = makeStyles<Theme, IUseSectionBasicsStylesProps>({
  carrierInputGrid: {
    display: ({ isCarrierInputVisible }) => (isCarrierInputVisible ? 'block' : 'none'),
  },
})

/**
 * Basics section in Create Load flow
 */
export const SectionBasics: FC = () => {
  const dispatch = useDispatch()
  const carriers = useSelector(selectCarriersList)
  const dispatchers = useSelector(dispatchersListSelector)
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')

  const { control, errors, getValues, setValue } = useCreateLoadFormContext()
  const { isAdminForCarrier = () => false } = useMembershipValidation()
  const { drivers = [] } = getValues()

  const isCarrierInputVisible = carriers?.length > 1

  /**
   * Make sure we have carriers before form is used,
   * @note This should only trigger once, on mount
   */
  const hasFetchedCarriers = useRef(false)
  useEffect(() => {
    if (!carriers.length && !hasFetchedCarriers.current) {
      dispatch(fetchCarriers())
      hasFetchedCarriers.current = true
    }
  }, [carriers.length, dispatch])

  /**
   * Set defaultDrivers on mount and make sure that RHF re-renders and
   * displays defaultDrivers in the sidebar summary
   */
  const hasSetDefaultDrivers = useRef(false)
  useEffect(() => {
    if (drivers.length && !hasSetDefaultDrivers.current) {
      setValue('drivers', drivers)
      hasSetDefaultDrivers.current = true
    }
  }, [drivers, setValue])

  /**
   * Set the default dispatcher to the current User if it's a new load
   * By default, the current user is the first dispatcher in the dispatchers list
   */
  const hasSetDefaultDispatcher = useRef(false)
  const [defaultDispatcher] = dispatchers || {}
  useEffect(() => {
    if (defaultDispatcher?.id && !hasSetDefaultDispatcher.current) {
      setValue('dispatcherId', defaultDispatcher.id)

      // only do this once
      hasSetDefaultDispatcher.current = true
    }
  }, [setValue, defaultDispatcher])

  /**
   * Set motorCarrier id on the shipment when
   * this component mounts
   *
   * Only continue to use if `fpCarrierPortal` is not yet enabled
   */
  const hasSetDefaultCarrier = useRef(false)
  const [defaultMotorCarrier] = carriers || []
  useEffect(() => {
    const { id } = (defaultMotorCarrier || {}) as IShipmentMotorCarrier
    if (!isFpCarrierPortalEnabled && !hasSetDefaultCarrier.current && typeof id !== 'undefined') {
      setValue('motorCarrierId', id)
      setValue('motorCarrier', defaultMotorCarrier)
      hasSetDefaultCarrier.current = true
    }
  }, [defaultMotorCarrier, isFpCarrierPortalEnabled, setValue])

  const classes = useSectionBasicsStyles({
    isCarrierInputVisible,
  })

  const { isMobile } = useBreakpointContext()

  const [isDispatcherInputVisible, setDispatcherInputVisible] = useState<boolean>(false)

  // If there's an error with the selected dispatcher, show the input
  useEffect(() => {
    if (errors.dispatcherId?.message) {
      setDispatcherInputVisible(true)
    }
  }, [errors.dispatcherId?.message])

  /**
   * Toggles dispatcher checkbox to reveal dispatcher select
   */
  const changeDispatcherCheckboxHandler = ({
    target: { checked },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setDispatcherInputVisible(!checked)

    if (defaultDispatcher?.id && checked) {
      setValue('dispatcherId', defaultDispatcher.id)
    }
  }

  /**
   * If there's an error with the dispatcher, we should display it
   */
  useEffect(() => {
    if (errors.dispatcherId?.message) {
      setDispatcherInputVisible(true)
    }
  }, [errors])

  const selectedMotorCarrierId = useWatch<CreateLoadSchemaType['motorCarrierId']>({
    name: 'motorCarrierId',
    control,
  })

  const isAdmin = selectedMotorCarrierId ? isAdminForCarrier(selectedMotorCarrierId) : true

  const selectedMotorCarrier = useMemo(
    () =>
      formatCarrier({
        id: selectedMotorCarrierId!,
        name: '',
      }),
    [selectedMotorCarrierId]
  )

  // When feature flag is on: show truck/driver/trailer fields if
  // a motor carrier has been selected
  const showTruckDriverAndTrailerFields = isFpCarrierPortalEnabled ? !!selectedMotorCarrierId : true

  /**
   * Fetches all equipment for trucks and trailer inputs
   */
  useEffectOnce(() => {
    dispatch(fetchEquipment())
  })

  return (
    <Box mb={3} pt={isMobile ? 0 : 2}>
      {/**
       * Register inputs for RHF to use
       */}
      <Grid id='section-basics' container spacing={3}>
        <Grid item xs={12} md={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SectionHeaderText>Basics</SectionHeaderText>
            </Grid>

            {/* Load ID */}
            <Grid item xs={12}>
              <DeprecatedControlledTextInput
                debounceMs={150}
                control={control}
                name='externalId'
                defaultValue=''
                TextInputProps={{
                  placeholder: 'Load ID #',
                  label: 'Load ID #',
                  dataTest: 'load-details-external-id',
                  large: isMobile,
                  validationMessage: errors?.externalId?.message,
                  invalid: !!errors?.externalId?.message,
                  required: true,
                  onBlur: () => {},
                }}
              />
            </Grid>

            {isFpCarrierPortalEnabled && (
              <Grid item xs={12} md={12}>
                <Grid container>
                  <Grid item xs={12} md={isFpCarrierPortalEnabled ? 12 : 6}>
                    <ControlledCarrier setValue={setValue} errors={errors} control={control} />
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Drivers */}
            {showTruckDriverAndTrailerFields && (
              <>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name='drivers'
                    defaultValue={[]}
                    render={({ onChange, value }) => {
                      const changeHandler = (change: Partial<ILoad>) => {
                        const { drivers: newDrivers = [] } = change || {}
                        onChange(newDrivers)
                      }

                      return (
                        <Driver
                          value={{ drivers: value }}
                          onChange={changeHandler}
                          layout={isMobile ? 'vertical' : 'horizontal'}
                          refetchOptions
                          large={isMobile}
                          errors={get(errors, 'drivers.message', '')}
                          isAddEntityEnabled={isFpCarrierPortalEnabled ? isAdmin : true}
                          /**
                           * Display Drivers in two groups depending on if
                           * they belong to current carrier
                           */
                          selectedMotorCarrier={
                            isFpCarrierPortalEnabled ? selectedMotorCarrier : undefined
                          }
                        />
                      )
                    }}
                  />
                </Grid>

                {/* Truck */}
                <Grid item xs={12} md={6}>
                  <Box mb={isMobile ? 2 : 0}>
                    <ControlledTruck
                      control={control}
                      setValue={setValue}
                      TruckProps={{
                        error: errors.truckId?.message,
                        large: isMobile,
                        refetchOptions: true,
                        selectedMotorCarrierId: isFpCarrierPortalEnabled
                          ? selectedMotorCarrierId
                          : undefined,
                      }}
                    />
                  </Box>
                </Grid>

                {/* Trailer */}
                <Grid item xs={12} md={6}>
                  <ControlledTrailer
                    control={control}
                    setValue={setValue}
                    TrailerProps={{
                      error: errors.trailerId?.message,
                      large: isMobile,
                      refetchOptions: true,
                      selectedMotorCarrierId: isFpCarrierPortalEnabled
                        ? selectedMotorCarrierId
                        : undefined,
                    }}
                  />
                </Grid>
              </>
            )}

            {/* Carrier - wait until there's a value to mount this */}
            {!isFpCarrierPortalEnabled && defaultMotorCarrier && (
              <Grid item xs={12} md={12} className={classes.carrierInputGrid}>
                <Grid container>
                  <Grid item xs={12} md={isFpCarrierPortalEnabled ? 12 : 6}>
                    <ControlledCarrier
                      setValue={setValue}
                      errors={errors}
                      control={control}
                      defaultValue={{
                        ...defaultMotorCarrier,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <Checkbox
                label="I'm the Dispatcher for this load"
                checked={!isDispatcherInputVisible}
                onChange={changeDispatcherCheckboxHandler}
              />
            </Grid>

            {/* Dispatcher */}
            <Grid item xs={12} md={6}>
              <ControlledDispatcher
                control={control}
                setValue={setValue}
                DispatcherProps={{
                  large: isMobile,
                  error: errors.dispatcherId?.message,
                  selectedMotorCarrierId,
                  shown: isDispatcherInputVisible,
                }}
                defaultValue={{
                  dispatcher: {
                    id: defaultDispatcher?.id,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}
